import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../axiosConfig';
import offlineManager from './utils/offlineManager';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ShoppingList from './ShoppingList';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    LinearProgress,
    Alert,
    CircularProgress,
    IconButton,
    Badge,
    Snackbar, 
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import SyncIcon from '@mui/icons-material/Sync';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; 
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const SoloDashboard = () => {
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState({
        todaysTakings: 0,
        weeklyTakings: 0,
        vatThreshold: null,
        taxPot: 0,
        holidayPay: 0,
        pensionReserve: 0,
        taxYearSummary: {
            totalIncome: 0,
            totalExpenses: 0,
            taxableProfit: 0,
            estimatedIncomeTax: 0,
            estimatedNI: 0,
            workingDays: 0,
            averageDailyRate: 0
        },
        recentActivity: []
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [taxYear, setTaxYear] = useState('2025/26');
    const [daysUntilDeadline, setDaysUntilDeadline] = useState(180);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [pendingCount, setPendingCount] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');
    const [shoppingBadge, setShoppingBadge] = useState(0);
    const [awaitingPatchTestCount, setAwaitingPatchTestCount] = useState(0);

    // Fetch dashboard data
    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);

        try {
            const timestamp = new Date().getTime();
            const response = await api.get(`/solo/dashboard?t=${timestamp}`);
            console.log('Solo dashboard data:', response.data);
            setDashboardData(response.data);
        } catch (err) {
            console.error('Error fetching solo dashboard data:', err);
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch count of online bookings awaiting a patch test
    const fetchAwaitingPatchTestCount = async () => {
        try {
            const res = await api.get('/solo/online-bookings?limit=0');
            setAwaitingPatchTestCount(res.data?.counts?.awaiting_patch_test || 0);
        } catch (err) {
            console.error('Failed to fetch patch test count:', err);
        }
    };

    // Update pending transaction count
    const updatePendingCount = async () => {
        try {
            const count = await offlineManager.getPendingCount();
            setPendingCount(count);
        } catch (error) {
            console.error('Error getting pending count:', error);
        }
    };

    // Sync pending transactions
    const syncPendingTransactions = async () => {
        if (!isOnline || syncing) return;

        setSyncing(true);
        try {
            const results = await offlineManager.syncAll(api);

            if (results.success > 0) {
                setSyncMessage(`✅ Synced ${results.success} transaction(s)`);
                await updatePendingCount();
                await fetchDashboardData();
            }

            if (results.failed > 0) {
                setSyncMessage(`⚠️ ${results.failed} transaction(s) failed to sync`);
            }
        } catch (error) {
            console.error('Error syncing:', error);
            setSyncMessage('❌ Sync failed');
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        updatePendingCount();
        fetchAwaitingPatchTestCount();

        const today = new Date();
        const deadline = new Date(today.getFullYear() + 1, 0, 31);
        const diffTime = deadline - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setDaysUntilDeadline(diffDays);

        const handleOnline = () => {
            setIsOnline(true);
            syncPendingTransactions();
        };

        const handleOffline = () => {
            setIsOnline(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Calculate reserves and available to draw
    const pensionReserve = dashboardData.pensionReserve ||
        (dashboardData.taxYearSummary.taxableProfit * 0.1);

    const totalReserves = dashboardData.taxPot + dashboardData.holidayPay + pensionReserve;
    const availableToDraw = Math.max(0, dashboardData.taxYearSummary.taxableProfit - totalReserves);

    // VAT threshold alerts
    const getVATAlertSeverity = (percentage) => {
        if (percentage >= 90) return 'error';
        if (percentage >= 75) return 'warning';
        return 'success';
    };

    const getVATProgressColor = (percentage) => {
        if (percentage >= 90) return 'error';
        if (percentage >= 75) return 'warning';
        return 'success';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchDashboardData}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
            {/* PWA Install Prompt */}
            <PWAInstallPrompt />

            {/* Offline/Sync Status Banner */}
            {!isOnline && (
                <Alert
                    severity="warning"
                    icon={<CloudOffIcon />}
                    sx={{ mb: 2 }}
                    action={
                        pendingCount > 0 && (
                            <Badge badgeContent={pendingCount} color="error">
                                <Typography variant="caption">{pendingCount} pending</Typography>
                            </Badge>
                        )
                    }
                >
                    You're offline. Transactions will sync when back online.
                </Alert>
            )}

            {pendingCount > 0 && isOnline && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            size="small"
                            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                            onClick={syncPendingTransactions}
                            disabled={syncing}
                        >
                            Sync Now
                        </Button>
                    }
                >
                    {pendingCount} transaction(s) waiting to sync
                </Alert>
            )}

            {/* Sync notification */}
            <Snackbar
                open={!!syncMessage}
                autoHideDuration={4000}
                onClose={() => setSyncMessage('')}
                message={syncMessage}
            />

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                        Solo Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Tax Year {taxYear} • {daysUntilDeadline} days until self-assessment deadline
                    </Typography>
                </Box>
                <Box>
                    <IconButton onClick={fetchDashboardData} color="primary" title="Refresh">
                        <RefreshIcon />
                    </IconButton>
                    {isOnline ? (
                        <IconButton color="success" title="Online">
                            <CloudDoneIcon />
                        </IconButton>
                    ) : (
                        <IconButton color="warning" title="Offline">
                            <CloudOffIcon />
                        </IconButton>
                    )}
                    <IconButton onClick={() => navigate('/solo/settings')} color="primary" title="Settings">
                        <SettingsIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Stats Grid */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Awaiting patch test — shown only when there are bookings needing attention */}
                {awaitingPatchTestCount > 0 && (
                    <Grid item xs={12} sm={6} md={4}>
                        <Card
                            sx={{
                                borderLeft: 4,
                                borderColor: 'warning.main',
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                            }}
                            onClick={() => navigate('/solo/online-bookings?status=awaiting_patch_test')}
                        >
                            <CardContent>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Awaiting patch test
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, my: 1, color: 'warning.main' }}>
                                    {awaitingPatchTestCount}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {awaitingPatchTestCount === 1 ? 'booking' : 'bookings'} needing a patch test arranged
                                </Typography>
                                <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
                                    Tap to review →
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Today's Takings */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ borderLeft: 4, borderColor: 'success.main', height: '100%' }}>
                        <CardContent>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Today's Takings
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, my: 1 }}>
                                £{dashboardData.todaysTakings.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                This week: £{dashboardData.weeklyTakings.toFixed(2)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

{/* VAT Threshold Tracker */}
{dashboardData.vatThreshold && (
    <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ borderLeft: 4, borderColor: 'error.main', height: '100%' }}>
            <CardContent>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    VAT Threshold Tracker
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, my: 1 }}>
                    £{dashboardData.vatThreshold.current.toLocaleString()}
                </Typography>

                {dashboardData.vatThreshold.threshold !== null ? (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            £{dashboardData.vatThreshold.remaining.toLocaleString()} until you must register for VAT
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={dashboardData.vatThreshold.percentage}
                            color={getVATProgressColor(dashboardData.vatThreshold.percentage)}
                            sx={{ height: 8, borderRadius: 1 }}
                        />
                        {dashboardData.vatThreshold.percentage >= 75 && (
                            <Alert severity={getVATAlertSeverity(dashboardData.vatThreshold.percentage)} sx={{ mt: 1, py: 0 }}>
                                ⚠️ {dashboardData.vatThreshold.percentage}% of the way to the VAT registration threshold
                            </Alert>
                        )}
                    </>
                ) : (
                    <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                        VAT threshold not configured. Contact support.
                    </Alert>
                )}
            </CardContent>
        </Card>
    </Grid>
)}
                

                {/* Tax Pot */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ borderLeft: 4, borderColor: 'warning.main', height: '100%' }}>
                        <CardContent>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Tax Pot (Estimated)
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, my: 1 }}>
                                £{dashboardData.taxPot.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Set aside 25% • Due: 31 Jan 2026
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                This is SL Solo's own estimate, not a figure confirmed by HMRC. For your official
                                tax calculation, check your HMRC Personal Tax Account.
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Pension Reserve */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ borderLeft: 4, borderColor: 'secondary.main', height: '100%' }}>
                        <CardContent>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Pension Reserve
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, my: 1 }}>
                                £{pensionReserve.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                10% of income recommended
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Gets 20% tax relief
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Holiday Pay Reserve */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ borderLeft: 4, borderColor: 'info.main', height: '100%' }}>
                        <CardContent>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Holiday Pay Reserve
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, my: 1 }}>
                                £{dashboardData.holidayPay.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                12.07% of earnings
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Available to Draw */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{
                        borderLeft: 4,
                        borderColor: availableToDraw > 0 ? 'success.main' : 'warning.main',
                        height: '100%',
                        background: availableToDraw > 0
                            ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                            : 'linear-gradient(135deg, #fff5e1 0%, #ffe4b5 100%)'
                    }}>
                        <CardContent>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                📊 Profit After Reserves
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, my: 1, color: availableToDraw > 0 ? 'success.main' : 'warning.main' }}>
                                £{availableToDraw.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                After all reserves set aside
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                ⚠️ Excludes unrecorded expenses & bills
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Quick Actions */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                    {
                        label: 'Record Sale',
                        sub: 'Log payment received',
                        icon: <AddIcon />,
                        path: '/solo/record-sale'
                    },
                    {
                        label: 'Add Expense',
                        sub: 'Track deductible costs',
                        icon: <ReceiptIcon />,
                        path: '/solo/add-expense'
                    },
                    {
                        label: 'Log Mileage',
                        sub: 'Business travel expenses',
                        icon: <DirectionsCarIcon />,
                        path: '/solo/log-mileage'
                    },
                    {
                        label: 'Import Data',
                        sub: 'Bulk upload records',
                        icon: <CloudUploadIcon />,
                        path: '/solo/import-data'
                    },
                    {
                        label: 'Self Assessment',
                        sub: 'MTD-ready tax return',
                        icon: <DescriptionIcon />,
                        path: '/solo/self-assessment'
                    },
                    {
                        label: 'Tax Settings',
                        sub: 'Update UTR, NINO, VAT',
                        icon: <AccountBalanceIcon />,
                        path: '/solo/settings'
                    },
                    {
                        label: 'Appointments',
                        sub: 'Manage your diary',
                        icon: <CalendarMonthIcon />,
                        path: '/solo/bookings'
                    },
                    {
                        label: 'Shopping list',
                        sub: 'Stock reorder alerts',
                        icon: (
                            <Badge badgeContent={shoppingBadge || null} color="error">
                                <ShoppingCartIcon />
                            </Badge>
                        ),
                        path: '/solo/shopping-list'
                    }
                ].map((action, i) => (
                    <Grid item xs={12} sm={6} md={i === 4 ? 12 : 3} key={i}>
                        <Button
                            fullWidth
                            variant={i === 4 ? 'contained' : 'outlined'}
                            startIcon={action.icon}
                            sx={{
                                py: 2,
                                justifyContent: 'flex-start',
                                borderWidth: 2,
                                '&:hover': { borderWidth: 2, borderColor: 'primary.main' },
                                ...(i === 4 && {
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                    }
                                })
                            }}
                            onClick={() => navigate(action.path)}
                        >
                            <Box sx={{ textAlign: 'left', ml: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {action.label}
                                </Typography>
                                <Typography variant="caption" color={i === 4 ? 'rgba(255,255,255,0.9)' : 'text.secondary'}>
                                    {action.sub}
                                </Typography>
                            </Box>
                        </Button>
                    </Grid>
                ))}
            </Grid>

            {/* Shopping List Widget */}
            <Box sx={{ mb: 3 }}>
                <ShoppingList
                    compact
                    onBadge={(count) => setShoppingBadge(count)}
                />
            </Box>

            {/* Tax Year Summary */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        This Tax Year Summary ({taxYear})
                    </Typography>
                    <Grid container spacing={2}>
                        {[
                            { label: 'Total Income', value: dashboardData.taxYearSummary.totalIncome },
                            { label: 'Total Expenses', value: dashboardData.taxYearSummary.totalExpenses },
                            { label: 'Taxable Profit', value: dashboardData.taxYearSummary.taxableProfit },
                            { label: 'Estimated Income Tax', value: dashboardData.taxYearSummary.estimatedIncomeTax },
                            { label: 'Estimated National Insurance', value: dashboardData.taxYearSummary.estimatedNI },
                            { label: 'Working Days This Year', value: `${dashboardData.taxYearSummary.workingDays} days` }
                        ].map((item, idx) => (
                            <Grid item xs={12} sm={6} md={4} key={idx}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {item.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {typeof item.value === 'number'
                                            ? `£${item.value.toLocaleString()}`
                                            : item.value}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5, bgcolor: 'grey.100', px: 2, borderRadius: 1, mt: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    Average Daily Rate
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                    £{dashboardData.taxYearSummary.averageDailyRate.toFixed(2)}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Recent Activity
                    </Typography>
                    {dashboardData.recentActivity.length > 0 ? (
                        dashboardData.recentActivity.map((activity, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    py: 1.5,
                                    borderBottom: index < dashboardData.recentActivity.length - 1 ? 1 : 0,
                                    borderColor: 'divider'
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {activity.description}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: activity.type === 'expense' ? 'error.main' : 'success.main' }}
                                >
                                    {activity.type === 'expense' ? '-' : ''}£{activity.amount.toFixed(2)}
                                </Typography>
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            No recent activity
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default SoloDashboard;